import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { replace } from 'connected-react-router'
import { common, layouts } from '@ocean/ui'
import { SessionUtils, ToastUtils } from '@ocean/utils'
import { UserUtils } from 'utils'
import { handleUnauthorizedEvent } from 'utils/EventUtils'
import { useLazySelector } from 'hooks'
import Skeleton, { SkeletonVariant } from 'components/common/Skeleton'
import contentPlayerRoutes from 'modules/AdaptiveLearningContentPlayer/routes'
import homeRoutes from 'modules/Home/routing/routes'
import { useAdaptiveLearningContext } from 'modules/requirements/Requirements/hooks/useAdaptiveLearningContext'
import { requirementsRoutes } from 'modules/requirements/Requirements/routing'
import {
  AdaptiveLearningEvent,
  AdaptiveLearningEvents,
} from 'modules/requirements/Requirements/types'
import libraryRoutes from 'modules/trainingLibrary/TrainingLibrary/routing/routes'

const { PageLayout, PageSegment } = layouts
const { Decorator } = common

const AssessmentPlayer: React.FC = () => {
  const [olpSourcePathname] = useState(() => {
    const pathname = sessionStorage.getItem('OLP_SOURCE_PATH')
    return pathname
  })
  const assessmentPlayerRef = useRef<HTMLIFrameElement>(null)
  const { isLoading, error, customerId } = useLazySelector(({ system }) => {
    const { isLoading, result, error } = system.customerInformation
    return {
      isLoading,
      customerId: result?.information.customerId,
      error,
    }
  })

  const dispatch = useDispatch()
  const adaptiveLearning = useAdaptiveLearningContext()

  useEffect(() => {
    if (!adaptiveLearning.isValid) {
      dispatch(replace(`${requirementsRoutes.root}`))
    }
  }, [adaptiveLearning.isValid, dispatch])

  useEffect(() => {
    const handlePostMessage = (e: MessageEvent<AdaptiveLearningEvent>) => {
      switch (e.data.type) {
        case AdaptiveLearningEvents.Disable: {
          dispatch(replace(`${requirementsRoutes.root}`, adaptiveLearning.buildContext(e.data)))
          break
        }

        case AdaptiveLearningEvents.ExitAssessmentPlayer: {
          dispatch(replace(`${requirementsRoutes.root}`, adaptiveLearning.buildContext(e.data)))
          break
        }

        case AdaptiveLearningEvents.ContentPlayer: {
          dispatch(replace(contentPlayerRoutes.root, adaptiveLearning.buildContext(e.data)))
          break
        }

        case AdaptiveLearningEvents.ExitHome: {
          dispatch(replace(`${homeRoutes.root}`, adaptiveLearning.buildContext(e.data)))
          break
        }

        case AdaptiveLearningEvents.ExitLibrary: {
          dispatch(replace(`${libraryRoutes.root}`, adaptiveLearning.buildContext(e.data)))
          break
        }
      }
    }
    window.addEventListener('message', handlePostMessage)
    const cleanupUnauthorizedEvent = handleUnauthorizedEvent(dispatch)

    return () => {
      window.removeEventListener('message', handlePostMessage)
      cleanupUnauthorizedEvent()
    }
  }, [adaptiveLearning, dispatch])

  const sendTokenAndUserInfo = () => {
    if (assessmentPlayerRef.current) {
      const sessionToken = SessionUtils.getSessionToken()
      const { connectedPerson, login } = UserUtils.getUserAccountInfo()
      assessmentPlayerRef.current.contentWindow?.postMessage(
        {
          type: 'OLP_ASSESSMENT_PLAYER',
          sessionToken,
          connectedPerson,
          username: login,
          customerId,
          olpSourcePathname: olpSourcePathname,
          ...adaptiveLearning.context,
        },
        window.cc.assessmentPlayer ?? ''
      )
      //? this is to hide track bars from the window
      const trackVerticalElements = document.querySelectorAll<HTMLElement>(
        '[data-selector="TrackVertical"]'
      )

      if (trackVerticalElements.length >= 2) {
        for (let i = trackVerticalElements.length - 1; i >= trackVerticalElements.length - 2; i--) {
          trackVerticalElements[i].style.visibility = 'hidden'
        }
      }
      const parentDiv = assessmentPlayerRef.current.parentElement
      if (parentDiv) {
        parentDiv.style.overflow = 'hidden'
      }
    }
  }

  if (isLoading) {
    return <Skeleton variant={SkeletonVariant.Details} />
  }

  if (error) {
    ToastUtils.error(error.error)
    return null
  }

  if (!adaptiveLearning.isValid) {
    return null
  }

  return (
    <PageLayout>
      <PageSegment segmentId='content'>
        <Decorator root backgroundColor='white' textColor='black'>
          <iframe
            title='Assessment Player'
            ref={assessmentPlayerRef}
            src={`${window.cc.assessmentPlayer}${adaptiveLearning.queryString}`}
            onLoad={sendTokenAndUserInfo}
            width='100%'
            height='100%'
            style={{ border: 'none', overflow: 'hidden' }}
          />
        </Decorator>
      </PageSegment>
    </PageLayout>
  )
}

export default AssessmentPlayer
